import { useCallback, useMemo, useRef, useState } from "react";
import { useWhatsAppCampaignWizard } from "src/layouts/Engagements/Campaigns/Tabs/WhatsAppCampaign/hooks/UseWhatsAppCampaignWizard";
import { useWhatsAppTemplates } from "src/layouts/Engagements/Campaigns/Tabs/WhatsAppCampaign/hooks/UseWhatsAppTemplates";
import { NO_LIST_NAMES } from "src/layouts/Engagements/Campaigns/Tabs/WhatsAppCampaign/WhatsAppCampaignConstants";
import type {
  WhatsAppCampaignStepKey,
  WhatsAppMediaState,
  WhatsAppTemplate,
  WhatsAppVariableDrafts,
} from "src/layouts/Engagements/Campaigns/Tabs/WhatsAppCampaign/WhatsAppCampaignTypes";

export interface ChatWhatsAppStepSnapshot {
  stepIndex: number;
  stepKey: WhatsAppCampaignStepKey;
  template: WhatsAppTemplate | null;
  drafts: WhatsAppVariableDrafts;
  media: WhatsAppMediaState;
}

interface UseChatWhatsAppCampaignWizardParams {
  useAi: boolean;
  onAdvance: (_snapshot: ChatWhatsAppStepSnapshot) => void;
  onBackToMode: () => void;
}

export const useChatWhatsAppCampaignWizard = ({
  useAi,
  onAdvance,
  onBackToMode,
}: UseChatWhatsAppCampaignWizardParams) => {
  const templatesState = useWhatsAppTemplates();
  const [campaignName, setCampaignName] = useState("");
  const [sendTo, setSendTo] = useState(NO_LIST_NAMES);
  const wizardBase = useWhatsAppCampaignWizard({
    listNames: sendTo,
    campaignName,
    onTemplatesStale: templatesState.reload,
  });
  const { stepIndex, stepKey, template, drafts, media } = wizardBase;
  const latestStepIndexRef = useRef(stepIndex);
  latestStepIndexRef.current = stepIndex;

  const snapshotBeforeAdvance = useCallback(
    (): ChatWhatsAppStepSnapshot => ({ stepIndex, stepKey, template, drafts, media }),
    [drafts, media, stepIndex, stepKey, template]
  );

  const goNext = useCallback(() => {
    const snapshot = snapshotBeforeAdvance();
    const stepIndexBeforeAdvance = stepIndex;
    wizardBase.goNext();
    if (latestStepIndexRef.current !== stepIndexBeforeAdvance) {
      onAdvance(snapshot);
    }
  }, [onAdvance, snapshotBeforeAdvance, stepIndex, wizardBase]);

  const selectTemplate = useCallback(
    (nextTemplate: WhatsAppTemplate) => {
      const snapshot = snapshotBeforeAdvance();
      const stepIndexBeforeAdvance = stepIndex;
      wizardBase.selectTemplate(nextTemplate);
      if (latestStepIndexRef.current !== stepIndexBeforeAdvance) {
        onAdvance(snapshot);
      }
    },
    [onAdvance, snapshotBeforeAdvance, stepIndex, wizardBase]
  );

  const goBack = useCallback(() => {
    if (stepIndex === 0) {
      onBackToMode();
      return;
    }
    wizardBase.goBack();
  }, [onBackToMode, stepIndex, wizardBase]);

  const { reset: resetBase } = wizardBase;
  const reset = useCallback(() => {
    resetBase();
    setCampaignName("");
    setSendTo(NO_LIST_NAMES);
  }, [resetBase]);

  const wizard = useMemo(
    () => ({ ...wizardBase, goNext, selectTemplate, goBack, reset }),
    [goBack, goNext, reset, selectTemplate, wizardBase]
  );

  return { templatesState, wizard, useAi, campaignName, sendTo, setCampaignName, setSendTo };
};
